import { useFlag } from '@unleash/proxy-client-react';
import React, { ReactElement, useState } from 'react';
import { starIcon } from 'assets/icons';

const Star = ({
  height, width, fill,
}: {
  height: number,
  width: number,
  fill: string,
}) => {
  return <svg
    fill={fill}
    height={height}
    viewBox="0 0 24 24"
    width={width}
  >
    <path
      d="M12,17.27L18.18,21l-1.64-7.03L22,9.24l-7.19-0.61L12,2L9.19,8.63L2,9.24l5.46,4.73L5.82,21L12,17.27z"
      fill={fill}
      // stroke="#000000"
      stroke="none"
      strokeMiterlimit="10"
      strokeWidth="1"
    />
  </svg>;
};

const RatingElement = (props: {
  iconSrc: string,
  ratingValue: number,
  rated: boolean,
  readonly: boolean,
  size: number,
  color: string,
  onClick?: () => void,
  onHover?: () => void,
  onBlur?: () => void,
}): ReactElement => {
  return <span className='inline-block'>
    <button
      className={props.readonly ? 'cursor-default' : ''}
      aria-label={props.readonly ? '' : `Rate as ${props.ratingValue}`}
      disabled={props.readonly}
      onClick={props.onClick}
      onMouseOver={props.onHover}
      onMouseOut={props.onBlur}
      type='button'
      data-locator='star'
    >
      <Star width={props.size} height={props.size} fill={props.color} />
    </button>
  </span>;
};

type RatingProps = {
  readonly?: boolean, 
  className?: string,
  size: number,
  iconSrc?: string,
  ratingValue: number,
  ratingTotal?: number,
  onClick?: (rate: number) => void,
  ratedColor?: string,
  unratedColor?: string,
  label?: string,
  id?: string,
}

const Rating = (props: RatingProps): ReactElement => {
  const hardSelector = useFlag('training-hard-rating-selectors');

  const {
    readonly, className, ratingValue, ratingTotal, iconSrc, size, ratedColor, unratedColor, onClick, id,
  } = {
    ratingTotal: 5,
    iconSrc: starIcon,
    readonly: false,
    ratedColor: '#ebc109',
    unratedColor: 'grey',
    className: '',
    ...props,
  };

  const [hoveredRating, setHoveredRating] = useState<number | undefined>(undefined);

  const onHover = (rate: number) => {
    setHoveredRating(rate);
  };

  const onBlur = () => {
    setHoveredRating(undefined);
  };

  return <span className={className} id={id} data-locator='rating'>
    {!hardSelector
      && <span className='hide-element' aria-label='Rating value' data-locator='rating-value'>{ratingValue}</span>}
    {[...Array(ratingTotal)].map((_, i) => i + 1).map(i => {
      const isRated = i <= (hoveredRating || ratingValue);
      return <RatingElement
        size={size}
        ratingValue={i}
        readonly={readonly}
        onClick={readonly ? undefined : onClick && (() => onClick(i))}
        onHover={readonly ? undefined : () => onHover(i)}
        onBlur={readonly ? undefined : () => onBlur()}
        key={i}
        iconSrc={iconSrc}
        rated={isRated}
        color={isRated ? ratedColor : unratedColor}
      />;
    })}
  </span>;
};

export default Rating;
